import { useMemo, useState } from 'react';
import { ChartFrame, Axes, ChartTooltip, ChartLegend, SERIES_COLORS } from './ChartFrame';
import {
  scaleLinear, niceTicks, extent, linePath, areaPath, nearestIndex,
  timeTicks, timeTickFormat, insertGaps,
} from './scales';
import { EmptyState } from '../ui/AsyncBoundary';

/**
 * Time series — the shape most of this product's history takes.
 *
 * Series are drawn in fixed slot order (blue, orange, aqua) and a series keeps
 * its colour when a filter removes its neighbours, so a reader who learned
 * "speed is blue" stays right.
 *
 * There is deliberately no second y-axis. Two measures on different scales get
 * two charts stacked as small multiples — a dual axis invents a correlation
 * that is not in the data, and is the single most common way a dashboard
 * misleads.
 */
export function TimeSeriesChart({
  series,                 // [{ key, label, points: [{ t, value }], format }]
  height = 220,
  area = false,
  yMin,
  formatValue = (v) => String(Math.round(v)),
  ariaLabel,
  emptyMessage = 'No readings in this range',
}) {
  const [hover, setHover] = useState(null);

  // Gaps are inserted before anything else looks at the points, so the line,
  // the area fill and the crosshair all agree about where the data stops.
  // Every series shares the same timestamps, so they break in the same places.
  const visible = useMemo(
    () =>
      series
        .filter((s) => s.points?.some((p) => p.value !== null && p.value !== undefined))
        .map((s) => ({ ...s, points: insertGaps(s.points) })),
    [series]
  );

  const model = useMemo(() => {
    if (!visible.length) return null;
    const allPoints = visible.map((s) => s.points);
    const [tMin, tMax] = extent(allPoints, (p) => p.t);
    const [vMin, vMax] = extent(allPoints, (p) => p.value);

    // Start at zero when the data nearly does — a floating baseline exaggerates
    // small movements and is how a flat week looks like a crisis.
    const lower = yMin !== undefined ? yMin : vMin > 0 && vMin < vMax * 0.5 ? 0 : vMin;
    const { ticks, domain } = niceTicks(lower, vMax, 4);
    return { tDomain: [tMin, tMax], yDomain: domain, yTicks: ticks };
  }, [visible, yMin]);

  if (!model) {
    return (
      <EmptyState
        icon="chart"
        title={emptyMessage}
        description="Widen the range, or check that the device was reporting."
      />
    );
  }

  const formatTime = timeTickFormat(model.tDomain[1] - model.tDomain[0]);
  const hoveredPoints = hover
    ? visible.map((s, i) => ({
        label: s.label,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        raw: s.points[hover.index]?.value,
        value:
          s.points[hover.index]?.value === null || s.points[hover.index]?.value === undefined
            ? '—'
            : (s.format || formatValue)(s.points[hover.index].value),
      }))
    : [];

  return (
    <div className="relative">
      <ChartFrame
        height={height}
        ariaLabel={ariaLabel}
        onLeave={() => setHover(null)}
        onHover={({ x, plotWidth, offsetLeft }) => {
          const xScale = scaleLinear(model.tDomain, [0, plotWidth]);
          const index = nearestIndex(visible[0].points, xScale, x);
          if (index < 0) return setHover(null);
          setHover({
            index,
            x: xScale(visible[0].points[index].t),
            pageX: xScale(visible[0].points[index].t) + offsetLeft,
            containerWidth: plotWidth + offsetLeft,
            time: visible[0].points[index].t,
          });
        }}
      >
        {({ plotWidth, plotHeight }) => {
          const x = scaleLinear(model.tDomain, [0, plotWidth]);
          const y = scaleLinear(model.yDomain, [plotHeight, 0]);
          const lastPoints = visible.map((s) =>
            [...s.points].reverse().find((p) => p.value !== null && p.value !== undefined)
          );

          return (
            <>
              <Axes
                plotWidth={plotWidth}
                plotHeight={plotHeight}
                yTicks={model.yTicks}
                y={y}
                formatY={formatValue}
                xTicks={timeTicks(model.tDomain, 5)}
                x={x}
                formatX={formatTime}
              />

              {/* Area wash at ~10%, single series only — stacked washes turn to
                  mud and stop reading as separate lines. */}
              {area && visible.length === 1 && (
                <path
                  d={areaPath(visible[0].points, x, y, plotHeight)}
                  fill={SERIES_COLORS[0]}
                  opacity={0.1}
                />
              )}

              {visible.map((s, index) => (
                <path
                  key={s.key}
                  d={linePath(s.points, x, y)}
                  fill="none"
                  stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}

              {hover && (
                <line
                  x1={hover.x}
                  x2={hover.x}
                  y1={0}
                  y2={plotHeight}
                  stroke="var(--line-strong)"
                  strokeWidth={1}
                  shapeRendering="crispEdges"
                />
              )}

              {/* Markers are >=8px across and carry a 2px ring in the surface
                  colour, so they stay legible where they cross a line. */}
              {(hover
                ? visible.map((s, i) => ({ point: s.points[hover.index], index: i }))
                : lastPoints.map((point, i) => ({ point, index: i }))
              ).map(({ point, index }) =>
                point && point.value !== null && point.value !== undefined ? (
                  <circle
                    key={`dot-${index}`}
                    cx={x(point.t)}
                    cy={y(point.value)}
                    r={4}
                    fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                    stroke="var(--surface)"
                    strokeWidth={2}
                  />
                ) : null
              )}
            </>
          );
        }}
      </ChartFrame>

      {hover && (
        <ChartTooltip
          x={hover.pageX}
          y={8}
          containerWidth={hover.containerWidth}
          title={new Date(hover.time).toLocaleString(undefined, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
          })}
          rows={hoveredPoints}
        />
      )}

      <ChartLegend
        className="mt-2 justify-center"
        series={visible.map((s, index) => ({
          label: s.label,
          color: SERIES_COLORS[index % SERIES_COLORS.length],
        }))}
      />
    </div>
  );
}
